/**
 * 商品マスター (products) の hooks。Phase A/B (商品ライン廃止対応)。
 */
import type {
  Product,
  ProductAudienceKind,
  ProductPriceTag,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface ProductRecord extends Product {
  id: string;
}

export function useProducts(opts?: {
  category?: string;
  audienceKind?: ProductAudienceKind;
  activeOnly?: boolean;
}) {
  return useQuery({
    queryKey: ['products', opts ?? null],
    queryFn: async (): Promise<ProductRecord[]> => {
      const constraints = [];
      if (opts?.activeOnly) constraints.push(where('isActive', '==', true));
      if (opts?.category) constraints.push(where('category', '==', opts.category));
      if (opts?.audienceKind)
        constraints.push(where('audienceKind', 'in', [opts.audienceKind, 'both']));
      constraints.push(orderBy('name', 'asc'));
      const snap = await getDocs(query(collection(db, 'products'), ...constraints));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Product) }));
    },
  });
}

export function useProduct(id: string | null | undefined) {
  return useQuery({
    queryKey: ['products', 'detail', id],
    enabled: !!id,
    queryFn: async (): Promise<ProductRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'products', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Product) };
    },
  });
}

interface UpsertProductInput {
  id?: string;
  name: string;
  modelName: string;
  variation?: string | null;
  sku: string;
  description?: string | null;
  imageUrl?: string | null;
  category: string;
  tags?: string[];
  audienceKind: ProductAudienceKind;
  suppliers?: Array<{
    name: string;
    contact?: string | null;
    leadTimeDays?: number | null;
    costPrice: number;
    isPrimary: boolean;
  }>;
  priceTable?: Array<{
    from: string;
    until?: string | null;
    priceExcludingTax: number;
    priceIncludingTax: number;
    tag: ProductPriceTag;
  }>;
  isActive: boolean;
}

const upsertProductFn = httpsCallable<
  UpsertProductInput,
  { id: string; sku: string }
>(functions, 'upsertProduct');

export function useUpsertProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertProductInput) => {
      const r = await upsertProductFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

const deleteProductFn = httpsCallable<
  { id: string; force?: boolean },
  { id: string }
>(functions, 'deleteProduct');

// ─── Bulk upsert (Excel 取込用) ───
interface BulkUpsertItem {
  name: string;
  modelName: string;
  variation?: string | null;
  sku: string;
  description?: string | null;
  imageUrl?: string | null;
  category: string;
  tags?: string[];
  audienceKind: ProductAudienceKind;
  isActive: boolean;
  suppliers?: Array<{
    name: string;
    contact?: string | null;
    leadTimeDays?: number | null;
    costPrice: number;
    isPrimary: boolean;
  }>;
  priceTable?: Array<{
    from: string;
    until?: string | null;
    priceExcludingTax: number;
    priceIncludingTax: number;
    tag: ProductPriceTag;
  }>;
}

interface BulkUpsertResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  entries: Array<{
    sku: string;
    status: 'created' | 'updated' | 'skipped';
    id?: string;
    reason?: string;
  }>;
}

const bulkUpsertProductsFn = httpsCallable<
  { items: BulkUpsertItem[] },
  BulkUpsertResult
>(functions, 'bulkUpsertProducts');

export function useBulkUpsertProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { items: BulkUpsertItem[] }) => {
      const r = await bulkUpsertProductsFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; force?: boolean }) => {
      const r = await deleteProductFn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

/** カテゴリ一覧 (重複排除) — フィルタUIで使用 */
export function useProductCategories() {
  const { data } = useProducts({ activeOnly: false });
  const set = new Set<string>();
  for (const p of data ?? []) {
    if (p.category) set.add(p.category);
  }
  return Array.from(set).sort();
}
